import { useState } from "react";
import { createDeck, shuffleDeck, dealCards } from "../utils/deck";
import { executeTurn } from "../utils/botRunner";
import checkHands from "../utils/checkHands";

const BLIND_LEVELS = [[1, 2], [2, 4], [3, 6], [5, 10], [10, 20], [15, 30], [20, 40], [40, 80]];

function getBlind(round) {
  return BLIND_LEVELS[Math.min(Math.floor(round / 10), BLIND_LEVELS.length - 1)];
}

function nextNonEliminatedIdx(players, fromIdx) {
  const n = players.length;
  for (let i = 1; i < n; i++) {
    const idx = (fromIdx + i) % n;
    if (players[idx].eliminatedAt === null) return idx;
  }
  return fromIdx;
}

function nextBettingIdx(players, fromIdx) {
  const n = players.length;
  for (let i = 1; i < n; i++) {
    const idx = (fromIdx + i) % n;
    const p = players[idx];
    if (p.eliminatedAt === null && !p.is_folded && !p.is_all_in) return idx;
  }
  return null;
}

function isBettingComplete(players, maxBet) {
  const betting = players.filter(
    (p) => p.eliminatedAt === null && !p.is_folded && !p.is_all_in
  );
  if (betting.length === 0) return true;
  return betting.every((p) => p.hasActed && p.current_bet === maxBet);
}

function compareHands(a, b) {
  if (a.rank !== b.rank) return a.rank - b.rank;
  for (let i = 0; i < Math.max(a.tiebreak.length, b.tiebreak.length); i++) {
    const av = a.tiebreak[i] ?? -1;
    const bv = b.tiebreak[i] ?? -1;
    if (av !== bv) return av - bv;
  }
  return 0;
}

function distributePot(state) {
  const { players, community, pot } = state;
  const active = players.filter((p) => p.eliminatedAt === null);
  const inRound = active.filter((p) => !p.is_folded);

  if (inRound.length === 1) {
    const winner = inRound[0];
    const roundResult = [{ id: winner.id, name: winner.config.name, amount: pot }];
    const newPlayers = players.map((p) =>
      p.id === winner.id ? { ...p, funds: p.funds + pot } : p
    );
    return { ...state, players: newPlayers, pot: 0, roundResult };
  }

  const allInCaps = [
    ...new Set(
      active
        .filter((p) => p.is_all_in && p.totalContributed > 0)
        .map((p) => p.totalContributed)
        .sort((a, b) => a - b)
    ),
  ];

  const potLevels = [];
  let prevCap = 0;
  for (const cap of allInCaps) {
    let amount = 0;
    const eligible = new Set();
    for (const p of active) {
      amount += Math.min(p.totalContributed, cap) - Math.min(p.totalContributed, prevCap);
      if (!p.is_folded && p.totalContributed >= cap) eligible.add(p.id);
    }
    if (amount > 0) potLevels.push({ amount, eligible });
    prevCap = cap;
  }

  let finalAmount = 0;
  const finalEligible = new Set();
  for (const p of active) {
    finalAmount += Math.max(0, p.totalContributed - prevCap);
    if (!p.is_folded && p.totalContributed > prevCap) finalEligible.add(p.id);
  }
  if (finalAmount > 0) potLevels.push({ amount: finalAmount, eligible: finalEligible });

  const winnings = {};
  for (const p of players) winnings[p.id] = 0;

  for (const potLevel of potLevels) {
    const eligible = active.filter((p) => potLevel.eligible.has(p.id));
    if (eligible.length === 0) continue;
    let potWinners;
    if (eligible.length === 1) {
      potWinners = eligible;
    } else {
      const evaluated = eligible.map((p) => ({
        player: p,
        result: checkHands([...p.cards, ...community]),
      }));
      let best = null;
      potWinners = [];
      for (const e of evaluated) {
        if (!best || compareHands(e.result, best) > 0) {
          best = e.result;
          potWinners = [e.player];
        } else if (compareHands(e.result, best) === 0) {
          potWinners.push(e.player);
        }
      }
    }
    const share = Math.floor(potLevel.amount / potWinners.length);
    const remainder = potLevel.amount - share * potWinners.length;
    let remainderGiven = false;
    for (const w of potWinners) {
      const extra = !remainderGiven ? ((remainderGiven = true), remainder) : 0;
      winnings[w.id] += share + extra;
    }
  }

  const roundResult = players
    .filter((p) => winnings[p.id] > 0)
    .map((p) => ({ id: p.id, name: p.config.name, amount: winnings[p.id] }));

  const newPlayers = players.map((p) => ({ ...p, funds: p.funds + winnings[p.id] }));
  return { ...state, players: newPlayers, pot: 0, roundResult };
}

function beginNextRound(state) {
  const players = state.players.map((p) =>
    p.eliminatedAt === null && p.funds === 0
      ? { ...p, eliminatedAt: state.round }
      : p
  );

  const activeCount = players.filter((p) => p.eliminatedAt === null).length;
  if (activeCount <= 1) {
    return { ...state, players, phase: "gameOver" };
  }

  const dealerIndex = nextNonEliminatedIdx(players, state.dealerIndex);
  return startRound({ ...state, players, dealerIndex, round: state.round + 1 });
}

function advanceAfterBetting(state) {
  const { step } = state;

  if (step === 3) {
    return { ...distributePot(state), phase: "showdown" };
  }

  let newCommunity, newDeck;
  if (step === 0) {
    newCommunity = [...state.community, ...state.deck.slice(0, 3)];
    newDeck = state.deck.slice(3);
  } else {
    newCommunity = [...state.community, state.deck[0]];
    newDeck = state.deck.slice(1);
  }

  const players = state.players.map((p) =>
    p.eliminatedAt === null ? { ...p, current_bet: 0, hasActed: false } : p
  );
  const newState = {
    ...state,
    players,
    community: newCommunity,
    deck: newDeck,
    step: step + 1,
    maxBet: 0,
    lastActedIndex: null,
  };

  const firstIdx = nextBettingIdx(players, newState.dealerIndex);
  if (firstIdx === null) {
    return advanceAfterBetting(newState);
  }

  return { ...newState, currentTurnIndex: firstIdx, phase: "betting" };
}

function startRound(state) {
  const active = state.players.filter((p) => p.eliminatedAt === null);
  let deck = shuffleDeck(createDeck());

  const cardMap = {};
  for (const p of active) {
    const { cards, remaining } = dealCards(deck, 2);
    cardMap[p.id] = cards;
    deck = remaining;
  }

  const [sb, bb] = getBlind(state.round);

  let players = state.players.map((p) => {
    if (p.eliminatedAt !== null) return p;
    return {
      ...p,
      current_bet: 0,
      totalContributed: 0,
      hasActed: false,
      cards: cardMap[p.id],
      is_folded: false,
      is_all_in: false,
      lastMessage: null,
      lastAction: null,
    };
  });

  const sbIdx = nextNonEliminatedIdx(players, state.dealerIndex);
  const bbIdx = nextNonEliminatedIdx(players, sbIdx);

  let pot = 0;
  let maxBet = 0;

  const applyBlind = (idx, amount) => {
    const p = players[idx];
    const actual = Math.min(amount, p.funds);
    pot += actual;
    players = players.map((pl, i) =>
      i === idx
        ? {
            ...pl,
            funds: pl.funds - actual,
            current_bet: pl.current_bet + actual,
            totalContributed: pl.totalContributed + actual,
            is_all_in: pl.funds - actual === 0,
          }
        : pl
    );
    maxBet = Math.max(maxBet, players[idx].current_bet);
  };

  applyBlind(sbIdx, sb);
  applyBlind(bbIdx, bb);

  const firstTurnIdx = nextBettingIdx(players, bbIdx);

  if (firstTurnIdx === null) {
    return advanceAfterBetting({
      ...state,
      players,
      deck,
      community: [],
      pot,
      step: 0,
      maxBet,
      phase: "betting",
      currentTurnIndex: null,
    });
  }

  return {
    ...state,
    players,
    deck,
    community: [],
    pot,
    step: 0,
    currentTurnIndex: firstTurnIdx,
    lastActedIndex: bbIdx,
    maxBet,
    phase: "betting",
  };
}

function initState(bots) {
  const players = bots.map((bot) => ({
    id: bot.id,
    botCode: bot.code,
    config: bot.config,
    funds: bot.funds,
    current_bet: 0,
    totalContributed: 0,
    hasActed: false,
    cards: [],
    is_folded: false,
    is_all_in: false,
    memory: null,
    lastMessage: null,
    lastAction: null,
    eliminatedAt: null,
  }));

  return startRound({
    players,
    community: [],
    deck: [],
    pot: 0,
    round: 0,
    step: 0,
    dealerIndex: 0,
    currentTurnIndex: null,
    lastActedIndex: null,
    maxBet: 0,
    phase: "betting",
  });
}

function getDisplayAction(actionType, newCurrentBet, prevMaxBet) {
  if (actionType === "fold") return "FOLD";
  if (actionType === "check") return "CHECK";
  if (actionType === "all_in") return "ALL-IN";
  if (newCurrentBet > prevMaxBet) return prevMaxBet === 0 ? "BET" : "RAISE";
  return "CALL";
}

export default function useGameState(bots) {
  const [state, setState] = useState(() => initState(bots));

  const nextTurn = () => {
    setState((prev) => {
      if (prev.phase === "showdown") {
        return beginNextRound(prev);
      }

      if (prev.phase === "transition") {
        return advanceAfterBetting(prev);
      }

      if (prev.phase !== "betting") return prev;

      const { currentTurnIndex, players, maxBet, pot } = prev;
      const player = players[currentTurnIndex];

      const { action, message, newMemory } = executeTurn(
        player.botCode,
        prev,
        currentTurnIndex,
        player.memory
      );

      let newPlayers = players.slice();
      let newPot = pot;
      let newMaxBet = maxBet;

      if (action.type === "fold") {
        newPlayers[currentTurnIndex] = {
          ...player,
          is_folded: true,
          hasActed: true,
          lastAction: "FOLD",
          lastMessage: message,
          memory: newMemory,
        };
      } else if (action.type === "check") {
        newPlayers[currentTurnIndex] = {
          ...player,
          hasActed: true,
          lastAction: "CHECK",
          lastMessage: message,
          memory: newMemory,
        };
      } else {
        const betAmount =
          action.type === "all_in" ? player.funds : action.value;
        const actual = Math.min(betAmount, player.funds);
        const newCurrentBet = player.current_bet + actual;
        const newFunds = player.funds - actual;
        const isAllIn = newFunds === 0;

        newPot += actual;
        const displayAction = getDisplayAction(action.type, newCurrentBet, newMaxBet);

        newPlayers[currentTurnIndex] = {
          ...player,
          funds: newFunds,
          current_bet: newCurrentBet,
          totalContributed: player.totalContributed + actual,
          is_all_in: isAllIn,
          hasActed: true,
          lastAction: displayAction,
          lastMessage: message,
          memory: newMemory,
        };

        if (newCurrentBet > newMaxBet) {
          newMaxBet = newCurrentBet;
          newPlayers = newPlayers.map((p, i) =>
            i !== currentTurnIndex &&
            p.eliminatedAt === null &&
            !p.is_folded &&
            !p.is_all_in
              ? { ...p, hasActed: false }
              : p
          );
        }
      }

      let newState = { ...prev, players: newPlayers, pot: newPot, maxBet: newMaxBet, lastActedIndex: currentTurnIndex };

      const inRound = newPlayers.filter(
        (p) => !p.is_folded && p.eliminatedAt === null
      );
      if (inRound.length <= 1) {
        return { ...distributePot(newState), phase: "showdown" };
      }

      if (isBettingComplete(newPlayers, newMaxBet)) {
        return { ...newState, phase: "transition" };
      }

      const nextIdx = nextBettingIdx(newPlayers, currentTurnIndex);
      return { ...newState, currentTurnIndex: nextIdx };
    });
  };

  return { state, nextTurn };
}
